import type { UseFormReturnType } from '@mantine/form'
import {
  TextInput,
  NumberInput,
  Radio,
  Checkbox,
  Paper,
  Divider,
  Button,
  Group,
  Stack,
  Text,
  Table,
  Badge,
  Collapse,
  Textarea,
} from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import { SPECIMEN_TYPES, PRIMARY_INDICATIONS, CANCER_TYPES, TEST_PANELS } from './formData'
import type { InvitaeFormState, CancerHistoryEntry } from './formData'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { FamilyHistorySection } from './shared/FamilyHistorySection'
import { PrimaryIndicationSection } from './shared/PrimaryIndicationSection'
import type { DiagnosisDefault } from './shared/types'

type Props = {
  form: UseFormReturnType<InvitaeFormState>
  expandedPanels: Set<string>
  togglePanel: (category: string) => void
  diagnosisDefaults: DiagnosisDefault[]
}

function createEmptyCancerEntry(type: string): CancerHistoryEntry {
  return {
    type,
    ageOfDiagnosis: '',
    breastOptions: [],
    msiHighOrIhcAbnormal: false,
    msiResult: '',
    additionalInfo: '',
    metastatic: '',
    gleasonScore: '',
    polypCount: '',
    pathology: '',
    cancerType: '',
  }
}

export const CancerSections = ({ form, expandedPanels, togglePanel, diagnosisDefaults }: Props) => {
  const toggleCancerType = (type: string) => {
    const exists = form.values.cancerHistory.find((c) => c.type === type)
    if (exists) {
      form.setFieldValue(
        'cancerHistory',
        form.values.cancerHistory.filter((c) => c.type !== type),
      )
    } else {
      form.setFieldValue('cancerHistory', [
        ...form.values.cancerHistory,
        createEmptyCancerEntry(type),
      ])
    }
  }

  const updateCancerEntry = (
    type: string,
    field: keyof CancerHistoryEntry,
    value: CancerHistoryEntry[keyof CancerHistoryEntry],
  ) => {
    form.setFieldValue(
      'cancerHistory',
      form.values.cancerHistory.map((c) => (c.type === type ? { ...c, [field]: value } : c)),
    )
  }

  const toggleTestSelection = (code: string) => {
    if (form.values.selectedTests.includes(code)) {
      form.setFieldValue(
        'selectedTests',
        form.values.selectedTests.filter((c) => c !== code),
      )
    } else {
      form.setFieldValue('selectedTests', [...form.values.selectedTests, code])
    }
  }

  return (
    <>
      {/* ===== HEREDITARY CANCER (hidden — specimen collection not shown to user) ===== */}
      {/* eslint-disable-next-line no-constant-binary-expression */}
      {false && (
        <Paper p="md" withBorder>
          <Text fw={600} size="lg" mb="sm" className="text-[#0097CE]">
            HEREDITARY CANCER
          </Text>
          <Radio.Group label="Specimen Type" {...form.getInputProps('specimenType')}>
            <Group>
              {SPECIMEN_TYPES.map((s) => (
                <Radio key={s.value} value={s.value} label={s.label} />
              ))}
            </Group>
          </Radio.Group>
          {form.values.specimenType === 'dna' && (
            <TextInput label="DNA Source" {...form.getInputProps('dnaSource')} mt="xs" w={300} />
          )}
          <TextInput
            label="Specimen Collection Date (MM/DD/YYYY)"
            placeholder="MM/DD/YYYY"
            {...form.getInputProps('specimenCollectionDate')}
            mt="sm"
            w={200}
          />
        </Paper>
      )}

      {/* ===== PRIMARY INDICATION ===== */}
      <PrimaryIndicationSection
        indications={PRIMARY_INDICATIONS}
        value={form.values.primaryIndication}
        onChange={(v) => form.setFieldValue('primaryIndication', v)}
        otherValue={form.values.primaryIndicationOther}
        onOtherChange={(v) => form.setFieldValue('primaryIndicationOther', v)}
      />

      {/* ===== ICD-10 CODES ===== */}
      <DiagnosisCodesSection
        value={form.values.diagnosisText}
        defaultCodes={diagnosisDefaults}
        onChange={(v) => form.setFieldValue('diagnosisText', v)}
      />

      {/* ===== PERSONAL HISTORY OF CANCER ===== */}
      <Paper p="md" withBorder>
        <Text fw={600} size="lg" mb="sm" className="text-[#0097CE]">
          PATIENT PERSONAL HISTORY OF CANCER
        </Text>
        <Checkbox
          label="No personal history of cancer"
          checked={form.values.noPersonalHistory}
          onChange={(e) => {
            form.setFieldValue('noPersonalHistory', e.target.checked)
            if (e.target.checked) form.setFieldValue('cancerHistory', [])
          }}
          mb="sm"
        />

        {!form.values.noPersonalHistory && (
          <Stack gap="xs">
            {CANCER_TYPES.map((ct) => {
              const entry = form.values.cancerHistory.find((c) => c.type === ct.value)
              const ageError = form.errors[`age_${ct.value}`]
              return (
                <div key={ct.value}>
                  <Checkbox
                    label={ct.label}
                    checked={!!entry}
                    onChange={() => toggleCancerType(ct.value)}
                  />
                  {entry && (
                    <Stack ml="lg" mt={4} gap={4}>
                      <NumberInput
                        size="xs"
                        label={
                          ct.value === 'polyps' ? 'Age of first diagnosis' : 'Age of diagnosis'
                        }
                        value={entry.ageOfDiagnosis === '' ? '' : Number(entry.ageOfDiagnosis)}
                        onChange={(v) => {
                          updateCancerEntry(ct.value, 'ageOfDiagnosis', String(v ?? ''))
                          if (ageError) {
                            form.clearFieldError(`age_${ct.value}`)
                          }
                        }}
                        error={ageError}
                        min={0}
                        max={200}
                        w={120}
                      />

                      {/* Breast: sub-checkboxes */}
                      {ct.value === 'breast' && (
                        <Checkbox.Group
                          value={entry.breastOptions}
                          onChange={(v) => updateCancerEntry(ct.value, 'breastOptions', v)}
                        >
                          <Group gap="sm" mt={2}>
                            <Checkbox
                              size="xs"
                              value="TNBC"
                              label="TNBC (triple negative: ER-, PR-, Her2-)"
                            />
                            <Checkbox
                              size="xs"
                              value="DCIS"
                              label="DCIS (ductal carcinoma in situ)"
                            />
                          </Group>
                          <Group gap="sm" mt={2}>
                            <Checkbox
                              size="xs"
                              value="IDC"
                              label="IDC (invasive ductal carcinoma)"
                            />
                            <Checkbox
                              size="xs"
                              value="ILC"
                              label="ILC (invasive lobular carcinoma)"
                            />
                          </Group>
                          <Group gap="sm" mt={2}>
                            <Checkbox
                              size="xs"
                              value="Bilateral"
                              label="Bilateral (two separate breast primaries)"
                            />
                          </Group>
                        </Checkbox.Group>
                      )}

                      {/* Endometrial: MSI checkbox + Result */}
                      {ct.value === 'endometrial' && (
                        <>
                          <Checkbox
                            size="xs"
                            label="Tumor is MSI-high or IHC abnormal"
                            checked={entry.msiHighOrIhcAbnormal}
                            onChange={(e) =>
                              updateCancerEntry(ct.value, 'msiHighOrIhcAbnormal', e.target.checked)
                            }
                          />
                          {entry.msiHighOrIhcAbnormal && (
                            <TextInput
                              size="xs"
                              label="Result"
                              value={entry.msiResult}
                              onChange={(e) =>
                                updateCancerEntry(ct.value, 'msiResult', e.target.value)
                              }
                              w={400}
                            />
                          )}
                        </>
                      )}

                      {/* Ovarian: Additional information */}
                      {ct.value === 'ovarian' && (
                        <TextInput
                          size="xs"
                          label="Additional information"
                          value={entry.additionalInfo}
                          onChange={(e) =>
                            updateCancerEntry(ct.value, 'additionalInfo', e.target.value)
                          }
                          w={400}
                        />
                      )}

                      {/* Prostate: Metastatic + Gleason */}
                      {ct.value === 'prostate' && (
                        <Group gap="sm">
                          <Radio.Group
                            label="Metastatic"
                            size="xs"
                            value={entry.metastatic}
                            onChange={(v) => updateCancerEntry(ct.value, 'metastatic', v)}
                          >
                            <Group gap="sm">
                              <Radio size="xs" value="yes" label="Yes" />
                              <Radio size="xs" value="no" label="No" />
                            </Group>
                          </Radio.Group>
                          <TextInput
                            size="xs"
                            label="Gleason score"
                            value={entry.gleasonScore}
                            onChange={(e) =>
                              updateCancerEntry(ct.value, 'gleasonScore', e.target.value)
                            }
                            w={150}
                          />
                        </Group>
                      )}

                      {/* Pancreatic: Additional information */}
                      {ct.value === 'pancreatic' && (
                        <TextInput
                          size="xs"
                          label="Additional information"
                          value={entry.additionalInfo}
                          onChange={(e) =>
                            updateCancerEntry(ct.value, 'additionalInfo', e.target.value)
                          }
                          w={400}
                        />
                      )}

                      {/* Colon/rectal: MSI checkbox + Result */}
                      {ct.value === 'colorectal' && (
                        <>
                          <Checkbox
                            size="xs"
                            label="Tumor is MSI-high or IHC abnormal"
                            checked={entry.msiHighOrIhcAbnormal}
                            onChange={(e) =>
                              updateCancerEntry(ct.value, 'msiHighOrIhcAbnormal', e.target.checked)
                            }
                          />
                          {entry.msiHighOrIhcAbnormal && (
                            <TextInput
                              size="xs"
                              label="Result"
                              value={entry.msiResult}
                              onChange={(e) =>
                                updateCancerEntry(ct.value, 'msiResult', e.target.value)
                              }
                              w={400}
                            />
                          )}
                        </>
                      )}

                      {/* Polyps: Cumulative polyp # + Pathology */}
                      {ct.value === 'polyps' && (
                        <>
                          <Radio.Group
                            label="Cumulative polyp #"
                            size="xs"
                            value={entry.polypCount}
                            onChange={(v) => updateCancerEntry(ct.value, 'polypCount', v)}
                          >
                            <Group gap="sm">
                              <Radio size="xs" value="1-9" label="1-9" />
                              <Radio size="xs" value="10-19" label="10-19" />
                              <Radio size="xs" value="20-99" label="20-99" />
                              <Radio size="xs" value="100+" label="100+" />
                            </Group>
                          </Radio.Group>
                          <TextInput
                            size="xs"
                            label="Pathology"
                            value={entry.pathology}
                            onChange={(e) =>
                              updateCancerEntry(ct.value, 'pathology', e.target.value)
                            }
                            w={300}
                          />
                        </>
                      )}

                      {/* Other: Type */}
                      {ct.value === 'other' && (
                        <TextInput
                          size="xs"
                          label="Type"
                          value={entry.cancerType}
                          onChange={(e) =>
                            updateCancerEntry(ct.value, 'cancerType', e.target.value)
                          }
                          w={400}
                        />
                      )}
                    </Stack>
                  )}
                </div>
              )
            })}
          </Stack>
        )}
      </Paper>

      {/* ===== FAMILY HISTORY OF CANCER ===== */}
      <FamilyHistorySection
        title="FAMILY HISTORY OF CANCER"
        familyHistory={form.values.familyHistory}
        onFamilyHistoryChange={(rows) => form.setFieldValue('familyHistory', rows)}
        noFamilyHistory={form.values.noFamilyHistory}
        onNoFamilyHistoryChange={(checked) => form.setFieldValue('noFamilyHistory', checked)}
        patientIsAdopted={form.values.patientIsAdopted}
        onPatientIsAdoptedChange={(checked) => form.setFieldValue('patientIsAdopted', checked)}
        noHistoryLabel="No known family history of cancer"
        conditionPlaceholder="e.g. Breast cancer"
      />

      {/* ===== TEST SELECTION ===== */}
      <Paper p="md" withBorder>
        <Text fw={600} size="lg" mb="sm" className="text-[#0097CE]">
          HEREDITARY CANCER TEST CATALOG
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Select test panels below. Test IDs containing add-on codes will include genes in both the
          original test and the add-on.
        </Text>

        <Stack gap="xs">
          {TEST_PANELS.map((panel) => (
            <div key={panel.category}>
              <Button
                variant="subtle"
                size="sm"
                fullWidth
                justify="space-between"
                rightSection={
                  expandedPanels.has(panel.category) ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )
                }
                onClick={() => togglePanel(panel.category)}
                className="!text-left"
              >
                <Group gap="xs">
                  <Text fw={600} size="sm">
                    {panel.category}
                  </Text>
                  <Badge size="xs" variant="light">
                    {panel.tests.length} tests
                  </Badge>
                </Group>
              </Button>

              {panel.note && expandedPanels.has(panel.category) && (
                <Text size="xs" c="orange" ml="md" mb="xs">
                  {panel.note}
                </Text>
              )}

              <Collapse in={expandedPanels.has(panel.category)}>
                <Table withTableBorder withColumnBorders ml="md" mr="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40} />
                      <Table.Th w={80}>Code</Table.Th>
                      <Table.Th>Test Name</Table.Th>
                      <Table.Th w={60}># Genes</Table.Th>
                      <Table.Th>Gene List</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {panel.tests.map((test) => (
                      <Table.Tr
                        key={test.code}
                        bg={'indent' in test && test.indent ? 'gray.0' : undefined}
                      >
                        <Table.Td>
                          <Checkbox
                            checked={form.values.selectedTests.includes(test.code)}
                            onChange={() => toggleTestSelection(test.code)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" className="font-mono">
                            {test.code}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" fw={500}>
                            {'indent' in test && test.indent ? '↳ ' : ''}
                            {test.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" ta="center">
                            {test.genes}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {test.geneList}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Collapse>
            </div>
          ))}
        </Stack>

        <Divider my="sm" />
        <Textarea
          label="Individual Gene Write-in"
          placeholder="Enter individual gene names..."
          {...form.getInputProps('individualGenes')}
        />
      </Paper>
    </>
  )
}
